import React, { useState } from 'react';
import ReactJWPlayer from 'react-jw-player';
import ProgressBar from '@ramsey-design-system/progress-bar';
import styles from './lesson-video-template.module.css';
import { ActionItem } from '../../../../types';
import throttle from 'lodash.throttle';
import { trackMediaInteraction } from '../../../../lib/learnClient';

type LessonVideoTemplateProps = {
  actionItem: ActionItem;
  onPlay?: () => void;
  onTime?: () => void;
  onPause?: () => void;
  onError?: () => void;
  onEnterFullScreen?: () => void;
  onExitFullScreen?: () => void;
  onMute?: () => void;
  onReady?: () => void;
  onResume?: () => void;
  onSetupError?: () => void;
  onUnmute?: () => void;
  onVideoLoad?: () => void;
  onNinetyFivePercent?: () => void;
  onOneHundredPercent?: () => void;
  customProps?: { [k: string]: unknown };
  onComplete: (actionItem: ActionItem) => void;
};

export function getVideoProgress(
  position: number | undefined,
  duration: number | undefined
) {
  if (position === undefined || duration === undefined) {
    return 0;
  }

  return (position / duration) * 100;
}

function getCaptionsUrl(actionItem: ActionItem) {
  if (actionItem.captions_url) {
    return actionItem.captions_url;
  } else {
    // TODO: HACK until we get this working in LearnAPI: Attempt to generate only for FPU
    // Example: "https://streaming.ramseysolutions.net/video/church/FPU/fpu_2022_edition_4_lesson_2.mp4/hls/fpu_2022_edition_4_lesson_2.m3u8"
    if (actionItem.video_url && actionItem.video_url.indexOf('fpu_2022') > -1) {
      const prefix =
        'https://cdn.ramseysolutions.net/media/ramsey-plus/app/learn/courses/financial-peace-university-v4/captions/';
      const videoFileName = actionItem.video_url.substring(
        actionItem.video_url.lastIndexOf('/') + 1
      );
      const videoFileNameNoExt = videoFileName.replace('.m3u8', '');
      return `${prefix}${videoFileNameNoExt}.vtt`;
    } else {
      return undefined;
    }
  }
}

function LessonVideoTemplate({
  actionItem,
  onError,
  onEnterFullScreen,
  onExitFullScreen,
  onMute,
  onSetupError,
  onUnmute,
  onVideoLoad,
  onNinetyFivePercent,
  onOneHundredPercent,
  customProps,
  onComplete,
}: LessonVideoTemplateProps) {
  const [isProgressBarVisible, setIsProgressBarVisible] = useState(true);
  const [videoComplete, setVideoComplete] = useState(false);
  const {
    id: actionId,
    course_id: courseId,
    lesson_id: lessonId,
    video_url: videoURL,
    preview_image: previewImage,
    position,
    duration,
    video_id: videoId,
    template,
  } = actionItem;

  const progress = getVideoProgress(position, duration);

  const hideProgressBar = () => setIsProgressBarVisible(false);

  const playerPayload = (
    playerState: 'play' | 'progress' | 'view' | 'pause'
  ) => {
    // @ts-ignore
    const player = jwplayer(`video-player-${lessonId}`);
    const videoProgress = Math.round(player.getPosition());
    if (
      playerState === 'progress' &&
      actionItem.completion_time &&
      !videoComplete
    ) {
      if (videoProgress >= actionItem.completion_time) {
        onComplete(actionItem);
        setVideoComplete(true);
        return;
      }
    }
    const isVisible = Boolean((player as any).getViewable());
    const hasCaptions = Boolean(player.getCurrentCaptions());
    const payload = {
      action: playerState,
      message: '',
      muted: player.getMute(),
      playback_rate: player.getPlaybackRate(),
      player_captions_visible: hasCaptions,
      player_fullscreen: player.getFullscreen(),
      player_visible: isVisible,
      position: videoProgress,
      video_vertical_resolution: player.getHeight(),
      volume: player.getVolume(),
    };

    trackMediaInteraction({
      course_id: courseId,
      lesson_id: lessonId,
      mediaId: videoId as string,
      template,
      action_id: actionId,
      data: payload,
    });
  };

  const playlistItem = {
    file: videoURL,
    image: previewImage,
    tracks: [] as any,
  };

  const captionsUrl = getCaptionsUrl(actionItem);
  if (captionsUrl) {
    playlistItem.tracks.push({
      file: captionsUrl,
      label: 'English',
      king: 'captions',
      default: false,
    });
  }

  return (
    <div className={styles.container} data-testid="video-container">
      <ReactJWPlayer
        playerId={`video-player-${lessonId}`}
        playerScript="https://cdn.jwplayer.com/libraries/L1H1ocUG.js"
        aspectRatio="16:9"
        playlist={[playlistItem]}
        customProps={customProps}
        onEnterFullScreen={onEnterFullScreen}
        onError={onError}
        onTime={throttle(() => playerPayload('progress'), 30000)} // TODO see if we can send to segment only every 5 minutes?
        onPause={() => playerPayload('pause')}
        onExitFullScreen={onExitFullScreen}
        onMute={onMute}
        onReady={() => playerPayload('view')}
        onResume={() => playerPayload('play')}
        onSetupError={onSetupError}
        onUnmute={onUnmute}
        onVideoLoad={onVideoLoad}
        onNinetyFivePercent={onNinetyFivePercent}
        onOneHundredPercent={onOneHundredPercent}
        onPlay={() => {
          hideProgressBar();
          // @ts-ignore
          jwplayer(`video-player-${lessonId}`).seek(actionItem.position);
        }}
      />

      {isProgressBarVisible && (
        <ProgressBar
          value={progress}
          size="large"
          squared
          color="alternate"
          className="ProgressBar"
        />
      )}
    </div>
  );
}

export default LessonVideoTemplate;
